import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Profile } from '@/types'
import {
  Search,
  Users,
  User,
  GraduationCap,
  Shield,
  Filter,
  Mail,
  // Calendar,
  MoreVertical,
  CheckCircle,
  XCircle,
  Key,
  Eye,
  EyeOff,
  X,
  Loader2,
  Star,
  UserPlus,
  Crown,
  Pencil,
  UserX,
  AlertCircle
} from 'lucide-react'

interface ProfileWithStudent extends Profile {
  subscription_status?: string | null
  program_start_date?: string | null
}

const ROLE_CONFIG = {
  admin: { label: 'Administrador', icon: Shield, color: 'bg-red-100 text-red-700' },
  teacher: { label: 'Profesor', icon: GraduationCap, color: 'bg-purple-100 text-purple-700' },
  student: { label: 'Estudiante', icon: User, color: 'bg-[#0055A4]/10 text-[#0055A4]' },
  parent: { label: 'Padre', icon: Users, color: 'bg-green-100 text-green-700' }
}

const SUBSCRIPTION_CONFIG: Record<string, { label: string; color: string }> = {
  active: { label: 'Premium', color: 'bg-yellow-100 text-yellow-800' },
  trial: { label: 'Trial', color: 'bg-[#0055A4]/10 text-[#0055A4]' },
  expired: { label: 'Expirado', color: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-600' }
}

export default function UsersList() {
  const [searchParams] = useSearchParams()
  const filterNoCohort = searchParams.get('filter') === 'no-cohort'

  const [users, setUsers] = useState<ProfileWithStudent[]>([])
  const [cohortStudentIds, setCohortStudentIds] = useState<Set<string>>(new Set())
  const [studentCohortMap, setStudentCohortMap] = useState<Map<string, string>>(new Map())
  const [dateCohortMap, setDateCohortMap] = useState<Map<string, string>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<string>(filterNoCohort ? 'student' : '')
  const [selectedUser, setSelectedUser] = useState<ProfileWithStudent | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [passwordModal, setPasswordModal] = useState<{ userId: string; userName: string } | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [deleteModal, setDeleteModal] = useState<{ userId: string; userName: string; userEmail: string } | null>(null)
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [dropdownUp, setDropdownUp] = useState(false)
  const menuButtonRef = (el: HTMLButtonElement | null, user: ProfileWithStudent) => {
    if (el && selectedUser?.id === user.id) {
      const rect = el.getBoundingClientRect()
      setDropdownUp(window.innerHeight - rect.bottom < 320)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      // Cargar perfiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (profilesError) throw profilesError

      // Cargar datos de estudiantes
      const { data: students } = await supabase
        .from('students')
        .select('profile_id, subscription_status, program_start_date')

      const studentMap: Record<string, { subscription_status: string; program_start_date: string | null }> = {}
      if (students) {
        for (const s of students as { profile_id: string; subscription_status: string; program_start_date: string | null }[]) {
          studentMap[s.profile_id] = { subscription_status: s.subscription_status, program_start_date: s.program_start_date }
        }
      }

      // Cargar student_ids de todas las cohortes
      const { data: cohorts } = await supabase
        .from('cohorts')
        .select('name, student_ids, start_date')

      const idsInCohort = new Set<string>()
      const cohortMap = new Map<string, string>()
      const dateCohortMap = new Map<string, string>()
      if (cohorts) {
        for (const c of cohorts as { name: string; student_ids: string[] | null; start_date: string | null }[]) {
          if (c.start_date && !dateCohortMap.has(c.start_date)) {
            dateCohortMap.set(c.start_date, c.name)
          }
          if (c.student_ids) {
            for (const id of c.student_ids) {
              idsInCohort.add(id)
              cohortMap.set(id, c.name)
            }
          }
        }
      }
      setCohortStudentIds(idsInCohort)
      setStudentCohortMap(cohortMap)
      setDateCohortMap(dateCohortMap)

      // Combinar datos
      const combined: ProfileWithStudent[] = (profiles || []).map((p: any) => ({
        ...p,
        subscription_status: studentMap[p.id]?.subscription_status || null,
        program_start_date: studentMap[p.id]?.program_start_date || null
      }))

      setUsers(combined)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRoleChange = async (userId: string, newRole: string) => {
    setIsUpdating(true)
    try {
      const { error } = await (supabase
        .from('profiles') as any)
        .update({ role: newRole })
        .eq('id', userId)

      if (error) throw error

      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole as any } : u))
      setSelectedUser(null)
    } catch (error) {
      console.error('Error updating role:', error)
      alert('Error al actualizar el rol')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    setIsUpdating(true)
    try {
      const { error } = await (supabase
        .from('profiles') as any)
        .update({ is_active: isActive })
        .eq('id', userId)

      if (error) throw error

      setUsers(users.map(u => u.id === userId ? { ...u, is_active: isActive } : u))
    } catch (error) {
      console.error('Error updating user:', error)
      alert('Error al actualizar el usuario')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleToggleSubscription = async (user: ProfileWithStudent) => {
    setIsUpdating(true)
    const newStatus = user.subscription_status === 'active' ? 'trial' : 'active'
    try {
      const { data, error } = await (supabase.rpc as any)('admin_update_subscription', {
        p_student_profile_id: user.id,
        p_subscription_status: newStatus
      })

      if (error) throw error
      if (data && !data.success) throw new Error(data.error)

      setUsers(users.map(u => u.id === user.id ? { ...u, subscription_status: newStatus } : u))
      setSelectedUser(null)
    } catch (error: any) {
      console.error('Error updating subscription:', error)
      alert('Error al actualizar suscripción: ' + (error.message || 'Error desconocido'))
    } finally {
      setIsUpdating(false)
    }
  }

  const openPasswordModal = (user: ProfileWithStudent) => {
    setPasswordModal({ userId: user.id, userName: `${user.first_name} ${user.last_name}` })
    setNewPassword('')
    setPasswordError('')
    setPasswordSuccess(false)
    setShowPassword(false)
    setSelectedUser(null)
  }

  const openDeleteModal = (user: ProfileWithStudent) => {
    setDeleteModal({
      userId: user.id,
      userName: `${user.first_name} ${user.last_name}`,
      userEmail: user.email || ''
    })
    setDeleteStep(1)
    setDeleteConfirmText('')
    setDeleteError('')
    setSelectedUser(null)
  }

  const closeDeleteModal = () => {
    setDeleteModal(null)
    setDeleteStep(1)
    setDeleteConfirmText('')
    setDeleteError('')
  }

  const handleDeleteStudent = async () => {
    if (!deleteModal) return

    setIsDeleting(true)
    setDeleteError('')

    try {
      const { data, error } = await (supabase.rpc as any)('admin_delete_student', {
        p_student_profile_id: deleteModal.userId
      })

      if (error) throw error
      if (data && !data.success) throw new Error(data.error)

      setUsers(users.filter(u => u.id !== deleteModal.userId))
      setCohortStudentIds(prev => {
        const next = new Set(prev)
        next.delete(deleteModal.userId)
        return next
      })
      setDeleteModal(null)
    } catch (error: any) {
      console.error('Error deleting student:', error)
      setDeleteError(error.message || 'Error al eliminar el estudiante')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleChangePassword = async () => {
    if (!passwordModal) return

    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres')
      return
    }

    setIsChangingPassword(true)
    setPasswordError('')

    try {
      const { error } = await (supabase.rpc as any)('admin_change_user_password', {
        target_user_id: passwordModal.userId,
        new_password: newPassword
      })

      if (error) throw error

      setPasswordSuccess(true)
      setTimeout(() => {
        setPasswordModal(null)
        setPasswordSuccess(false)
      }, 2000)
    } catch (error: any) {
      console.error('Error changing password:', error)
      setPasswordError(error.message || 'Error al cambiar la contraseña')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = !filterRole || user.role === filterRole
    const matchesNoCohort = !filterNoCohort || (user.role === 'student' && !cohortStudentIds.has(user.id))
    return matchesSearch && matchesRole && matchesNoCohort
  })

  const stats = {
    total: users.length,
    students: users.filter(u => u.role === 'student').length,
    teachers: users.filter(u => u.role === 'teacher').length,
    admins: users.filter(u => u.role === 'admin').length,
    premium: users.filter(u => u.subscription_status === 'active').length
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-gray-600">Gestiona los usuarios del sistema</p>
        </div>
        <Link
          to="/admin/students/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          Crear alumnos
        </Link>
      </div>

      {/* Banner filtro sin cohorte */}
      {filterNoCohort && (
        <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-xl text-teal-800 text-sm">
          <UserX className="w-4 h-4 shrink-0" />
          <span>Mostrando solo alumnos que <strong>no pertenecen a ninguna cohorte</strong>.</span>
          <Link to="/admin/users" className="ml-auto text-teal-600 hover:underline whitespace-nowrap">Ver todos</Link>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100">
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <User className="w-5 h-5 text-[#0055A4]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.students}</p>
              <p className="text-sm text-gray-500">Estudiantes</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100">
              <Crown className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.premium}</p>
              <p className="text-sm text-gray-500">Premium</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <GraduationCap className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.teachers}</p>
              <p className="text-sm text-gray-500">Profesores</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.admins}</p>
              <p className="text-sm text-gray-500">Admins</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] appearance-none bg-white"
          >
            <option value="">Todos los roles</option>
            <option value="student">Estudiantes</option>
            <option value="teacher">Profesores</option>
            <option value="admin">Administradores</option>
            <option value="parent">Padres</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Suscripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inicio programa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cohorte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const roleConfig = ROLE_CONFIG[user.role as keyof typeof ROLE_CONFIG] || ROLE_CONFIG.student
                  const RoleIcon = roleConfig.icon
                  const subConfig = user.subscription_status
                    ? SUBSCRIPTION_CONFIG[user.subscription_status] || null
                    : null
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm ${roleConfig.color}`}>
                          <RoleIcon className="w-4 h-4" />
                          {roleConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.role === 'student' || user.role === 'parent' ? (
                          subConfig ? (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${subConfig.color}`}>
                              {user.subscription_status === 'active' && <Star className="w-3 h-3" />}
                              {subConfig.label}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Sin datos</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.role === 'student' ? (
                          user.program_start_date ? (
                            <span className="text-sm text-gray-700">
                              {new Date(user.program_start_date + 'T12:00:00').toLocaleDateString('es-CL')}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Sin fecha</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.role === 'student' ? (
                          studentCohortMap.has(user.id) ? (
                            <span className="text-sm text-gray-700">{studentCohortMap.get(user.id)}</span>
                          ) : user.program_start_date && dateCohortMap.has(user.program_start_date) ? (
                            <span className="text-sm text-gray-700">{dateCohortMap.get(user.program_start_date)}</span>
                          ) : (
                            <span className="text-xs text-gray-400">Sin cohorte</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(user.id, !user.is_active)}
                          disabled={isUpdating}
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                            user.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {user.is_active ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              Activo
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3" />
                              Inactivo
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="relative">
                          <button
                            ref={(el) => menuButtonRef(el, user)}
                            onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {selectedUser?.id === user.id && (
                            <>
                              <span
                                className="fixed inset-0 z-40"
                                onClick={() => setSelectedUser(null)}
                              />
                              <div className={`absolute right-0 w-52 bg-white rounded-lg shadow-lg border z-50 ${dropdownUp ? 'bottom-full mb-2' : 'mt-2'}`}>
                                <div className="p-2">
                                  {/* Toggle Premium - solo para estudiantes */}
                                  {(user.role === 'student' || user.role === 'parent') && (
                                    <>
                                      <button
                                        onClick={() => handleToggleSubscription(user)}
                                        disabled={isUpdating}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-gray-100 text-gray-700"
                                      >
                                        {user.subscription_status === 'active' ? (
                                          <>
                                            <XCircle className="w-4 h-4 text-orange-500" />
                                            Desactivar Premium
                                          </>
                                        ) : (
                                          <>
                                            <Star className="w-4 h-4 text-yellow-500" />
                                            Activar Premium
                                          </>
                                        )}
                                      </button>
                                      <hr className="my-1" />
                                    </>
                                  )}

                                  <Link
                                    to={`/admin/users/${user.id}/edit`}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-gray-100 text-gray-700"
                                    onClick={() => setSelectedUser(null)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                    Editar datos
                                  </Link>
                                  <button
                                    onClick={() => openPasswordModal(user)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-gray-100 text-gray-700"
                                  >
                                    <Key className="w-4 h-4" />
                                    Cambiar contraseña
                                  </button>
                                  {user.role === 'student' && (
                                    <>
                                      <hr className="my-1" />
                                      <button
                                        onClick={() => openDeleteModal(user)}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-red-50 text-red-600"
                                      >
                                        <UserX className="w-4 h-4" />
                                        Eliminar estudiante
                                      </button>
                                    </>
                                  )}
                                  <hr className="my-1" />
                                  <p className="px-3 py-1 text-xs text-gray-500 uppercase">Cambiar rol</p>
                                  {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                                    <button
                                      key={role}
                                      onClick={() => handleRoleChange(user.id, role)}
                                      disabled={isUpdating || user.role === role}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg ${
                                        user.role === role
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : 'hover:bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      <config.icon className="w-4 h-4" />
                                      {config.label}
                                      {user.role === role && (
                                        <CheckCircle className="w-4 h-4 ml-auto text-green-500" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No se encontraron usuarios</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal cambiar contraseña */}
      {passwordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5 text-[#0055A4]" />
                Cambiar contraseña
              </h3>
              <button
                onClick={() => setPasswordModal(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                Nueva contraseña para <strong>{passwordModal.userName}</strong>
              </p>

              {passwordSuccess ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  Contraseña actualizada correctamente
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value)
                        setPasswordError('')
                      }}
                      placeholder="Nueva contraseña (mín. 6 caracteres)"
                      className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                      onKeyDown={(e) => e.key === 'Enter' && handleChangePassword()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {passwordError && (
                    <p className="text-sm text-red-600">{passwordError}</p>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setPasswordModal(null)}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleChangePassword}
                      disabled={isChangingPassword || !newPassword}
                      className="px-4 py-2 text-sm bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isChangingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                      Confirmar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal eliminar estudiante - Paso 1 */}
      {deleteModal && deleteStep === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-red-600 flex items-center gap-2">
                <UserX className="w-5 h-5" />
                Eliminar estudiante (Paso 1 de 2)
              </h3>
              <button
                onClick={closeDeleteModal}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
                <p className="text-sm text-red-800 font-semibold">
                  Estás a punto de eliminar permanentemente al siguiente estudiante:
                </p>
                <div className="text-sm text-red-700 bg-white/60 rounded p-2">
                  <p>Nombre: <strong>{deleteModal.userName}</strong></p>
                  <p>Email: <strong>{deleteModal.userEmail}</strong></p>
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-amber-800 mb-2">
                  Al eliminar este estudiante se borraran de forma permanente:
                </p>
                <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li>Su cuenta de acceso a la plataforma (no podrá volver a ingresar)</li>
                  <li>Todo su progreso de unidades, lecciones y contenidos</li>
                  <li>Todas sus respuestas a ejercicios y calificaciones recibidas</li>
                  <li>Sus registros de asistencia a talleres</li>
                  <li>Todos sus badges, puntos acumulados y transacciones de puntos</li>
                  <li>Sus respuestas a encuestas y popups</li>
                  <li>Sus notificaciones y logs de automatizacion</li>
                  <li>Será removido de todas las cohortes a las que pertenece</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                Esta acción es <strong>completamente irreversible</strong>. No será posible recuperar ninguno de estos datos una vez eliminados.
              </p>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => setDeleteStep(2)}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  Entiendo, continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal eliminar estudiante - Paso 2: confirmacion final */}
      {deleteModal && deleteStep === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-red-600 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Confirmación final (Paso 2 de 2)
              </h3>
              <button
                onClick={closeDeleteModal}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  Vas a eliminar a <strong>{deleteModal.userName}</strong> ({deleteModal.userEmail}).
                  Todos sus datos serán borrados permanentemente y su cuenta dejará de existir.
                </p>
              </div>

              <div>
                <label htmlFor="delete-confirm" className="block text-sm font-medium text-gray-700 mb-2">
                  Para confirmar, escribe <strong className="text-red-600">ELIMINAR</strong> en el campo de abajo:
                </label>
                <input id="delete-confirm" type="text" value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Escribe ELIMINAR para confirmar"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  autoFocus
                />
              </div>

              {deleteError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {deleteError}
                </div>
              )}

              <div className="flex gap-3 justify-between pt-2">
                <button
                  onClick={() => { setDeleteStep(1); setDeleteConfirmText('') }}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Volver
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={closeDeleteModal}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteStudent}
                    disabled={isDeleting || deleteConfirmText !== 'ELIMINAR'}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Eliminar permanentemente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
